// frontend/src/components/FacturesList.js
import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Modal, Form, Alert, Spinner, Badge, Card, Row, Col } from 'react-bootstrap';
import api from '../api';
import { exportFactures } from '../utils/exportExcel';

const STATUTS = ['brouillon', 'envoyee', 'payee', 'en_retard', 'annulee'];

const STATUT_BADGE = {
  brouillon:  { bg: 'secondary', label: 'Brouillon' },
  envoyee:    { bg: 'primary',   label: 'Envoyée' },
  payee:      { bg: 'success',   label: 'Payée' },
  en_retard:  { bg: 'danger',    label: 'En retard' },
  annulee:    { bg: 'dark',      label: 'Annulée' },
};

const EMPTY_FORM = {
  devis_id:      '',
  chantier_id:   '',
  client_id:     '',
  statut:        'brouillon',
  date_emission: new Date().toISOString().substring(0, 10),
  date_echeance: '',
  date_paiement: '',
  montant_ht:    '',
  tva:           20,
  notes:         ''
};
// ✅ Composant icône de tri
const SortIcon = ({ field, sortField, sortDir }) => {
  if (sortField !== field) return <span className="text-muted ms-1">↕</span>;
  return <span className="ms-1">{sortDir === 'asc' ? '↑' : '↓'}</span>;
};
function FacturesList({ onFactureChange }) {
  const [factures, setFactures]           = useState([]);
  const [chantiers, setChantiers]         = useState([]);
  const [devisList, setDevisList]         = useState([]);
  const [stats, setStats]                 = useState(null);
  const [loading, setLoading]             = useState(true);
  const [error, setError]                 = useState(null);
  const [showModal, setShowModal]         = useState(false);
  const [showConfirm, setShowConfirm]     = useState(false);
  const [showApercu, setShowApercu]       = useState(false);
  const [factureApercu, setFactureApercu] = useState(null);
  const [editFacture, setEditFacture]     = useState(null);
  const [factureToDelete, setFactureToDelete] = useState(null);
  const [form, setForm]                   = useState(EMPTY_FORM);
  const [submitting, setSubmitting]       = useState(false);
  const [filtreStatut, setFiltreStatut]   = useState('');
  const [pdfLoading, setPdfLoading]       = useState(null);
  const [relanceLoading, setRelanceLoading] = useState(false);
  const [sortField, setSortField] = useState('date_emission');
  const [sortDir, setSortDir]     = useState('desc');

  const handleSort = (field) => {
  if (sortField === field) {
    setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
  } else {
    setSortField(field);
    setSortDir('asc');
  }
};

const sortedFactures = [...factures].sort((a, b) => {
  let valA = a[sortField] || '';
  let valB = b[sortField] || '';
  if (typeof valA === 'string') valA = valA.toLowerCase();
  if (typeof valB === 'string') valB = valB.toLowerCase();
  if (valA < valB) return sortDir === 'asc' ? -1 : 1;
  if (valA > valB) return sortDir === 'asc' ? 1 : -1;
  return 0;
});
  const montantTTC = form.montant_ht && form.tva
    ? (parseFloat(form.montant_ht) * (1 + parseFloat(form.tva) / 100)).toFixed(2)
    : '0.00';
 
  const fetchFactures = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = filtreStatut ? `?statut=${filtreStatut}` : '';
      const [facturesRes, statsRes] = await Promise.all([
        api.get(`/factures${params}`),
        api.get('/factures/stats')
      ]);
      setFactures(facturesRes.data);
      setStats(statsRes.data);
      if (onFactureChange) onFactureChange(facturesRes.data.length);
    } catch (err) {
      setError(`Erreur: ${err.response?.data?.error || err.message}`);
    } finally {
      setLoading(false);
    }
  }, [filtreStatut]);

  const fetchChantiers = useCallback(async () => {
    try {
      const [chantiersRes, devisRes] = await Promise.all([
        api.get('/chantiers'),
        api.get('/devis')
      ]);
      setChantiers(chantiersRes.data);
      setDevisList(devisRes.data);
    } catch (err) {
      console.error('Erreur chargement données:', err);
    }
  }, []);

  useEffect(() => { fetchFactures(); },  [fetchFactures]);
  useEffect(() => { fetchChantiers(); }, [fetchChantiers]);

  const handleChantierChange = (e) => {
    const chantier_id = e.target.value;
    const chantier    = chantiers.find(c => c.id === parseInt(chantier_id));
    setForm({ ...form, chantier_id, client_id: chantier ? chantier.client_id : '', devis_id: '' });
  };

  const handleDevisChange = (e) => {
    const devis_id = e.target.value;
    const devis    = devisList.find(d => d.id === parseInt(devis_id));
    setForm({ ...form, devis_id, montant_ht: devis ? devis.montant_ht : form.montant_ht, tva: devis ? devis.tva : form.tva });
  };

  const handleField = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const openCreateModal = () => {
    setEditFacture(null);
    setForm(EMPTY_FORM);
    setError(null);
    setShowModal(true);
  };

  const openEditModal = (f) => {
    setEditFacture(f);
    setForm({
      devis_id:      f.devis_id     || '',
      chantier_id:   f.chantier_id,
      client_id:     f.client_id,
      statut:        f.statut,
      date_emission: f.date_emission ? f.date_emission.substring(0, 10) : '',
      date_echeance: f.date_echeance ? f.date_echeance.substring(0, 10) : '',
      date_paiement: f.date_paiement ? f.date_paiement.substring(0, 10) : '',
      montant_ht:    f.montant_ht || '',
      tva:           f.tva        || 20,
      notes:         f.notes      || ''
    });
    setError(null);
    setShowModal(true);
  };

  // ✅ Aperçu facture
  const openApercu = (f) => {
    setFactureApercu(f);
    setShowApercu(true);
  };

  // ✅ Téléchargement PDF
  const downloadPdf = async (f) => {
    setPdfLoading(f.id);
    try {
      const token    = localStorage.getItem('token');
      const response = await fetch(`/api/factures/${f.id}/pdf`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Erreur génération PDF');
      const blob = await response.blob();
      const url  = window.URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `${f.numero_facture}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(`Erreur PDF: ${err.message}`);
    } finally {
      setPdfLoading(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      if (editFacture) {
        await api.put(`/factures/${editFacture.id}`, form);
      } else {
        await api.post('/factures', form);
      }
      setShowModal(false);
      await fetchFactures();
    } catch (err) {
      setError(`Erreur: ${err.response?.data?.error || err.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/factures/${factureToDelete.id}`);
      setShowConfirm(false);
      setFactureToDelete(null);
      await fetchFactures();
    } catch (err) {
      setShowConfirm(false);
      setError(`Erreur: ${err.response?.data?.error || err.message}`);
    }
  };

  const formatMontant = (val) =>
    val ? `${Number(val).toLocaleString('fr-FR')} €` : '-';

  const formatDate = (val) =>
    val ? val.substring(0, 10).split('-').reverse().join('/') : '-';

  const devisFiltres = form.chantier_id
    ? devisList.filter(d => d.chantier_id === parseInt(form.chantier_id))
    : devisList;

  const handleRelancer = async () => {
    if (!window.confirm('Envoyer des relances par email pour toutes les factures en retard ?')) return;
    setRelanceLoading(true);
    try {
      const res = await fetch('/api/notifications/factures/relancer', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Erreur');
      alert(data.message);
      await fetchFactures();
    } catch (err) {
      setError(`Erreur: ${err.message}`);
    } finally {
      setRelanceLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center mt-5">
        <Spinner animation="border" />
        <p className="mt-2">Chargement des factures...</p>
      </div>
    );
  }

  return (
    <>
      {error && (
        <Alert variant="danger" className="mt-3" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Tableau de bord financier */}
      {stats && (
        <Row className="mb-4 mt-3">
          <Col md={3}>
            <Card bg="success" text="white" className="text-center">
              <Card.Body>
                <Card.Title>💰 Encaissé</Card.Title>
                <h4>{formatMontant(stats.total_encaisse)}</h4>
                <small>{stats.nb_payees} facture(s)</small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card bg="primary" text="white" className="text-center">
              <Card.Body>
                <Card.Title>📤 En attente</Card.Title>
                <h4>{formatMontant(stats.total_en_attente)}</h4>
                <small>{stats.nb_en_attente} facture(s)</small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card bg="danger" text="white" className="text-center">
              <Card.Body>
                <Card.Title>⚠️ En retard</Card.Title>
                <h4>{formatMontant(stats.total_en_retard)}</h4>
                <small>{stats.nb_en_retard} facture(s)</small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card bg="dark" text="white" className="text-center">
              <Card.Body>
                <Card.Title>📋 Total</Card.Title>
                <h4>{stats.total}</h4>
                <small>factures</small>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* Barre d'outils */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="d-flex align-items-center gap-3">
          <h4 className="mb-0">Factures ({factures.length})</h4>
          <Form.Select size="sm" style={{ width: '160px' }}
            value={filtreStatut} onChange={(e) => setFiltreStatut(e.target.value)}>
            <option value="">Tous les statuts</option>
            {STATUTS.map(s => (
              <option key={s} value={s}>{STATUT_BADGE[s].label}</option>
            ))}
          </Form.Select>
        </div>
        {stats?.nb_en_retard > 0 && (
          <Button variant="danger" onClick={handleRelancer} disabled={relanceLoading} className="me-2">
            {relanceLoading ? '⏳ Envoi...' : `🔔 Relancer (${stats.nb_en_retard})`}
          </Button>
        )}
        <Button variant="outline-success" className="me-2" onClick={() => exportFactures(factures)}>📊 Excel</Button>
        <Button variant="primary" onClick={openCreateModal}>+ Nouvelle Facture</Button>
      </div>

      {factures.length === 0 ? (
        <Alert variant="info">Aucune facture pour le moment.</Alert>
      ) : (
        <Table striped bordered hover responsive>
  <thead className="table-dark">
    <tr>
      <th>N°</th>
      <th style={{ cursor: 'pointer' }} onClick={() => handleSort('numero_facture')}>
        Référence <SortIcon field="numero_facture" sortField={sortField} sortDir={sortDir} />
      </th>
      <th style={{ cursor: 'pointer' }} onClick={() => handleSort('chantier_nom')}>
        Chantier <SortIcon field="chantier_nom" sortField={sortField} sortDir={sortDir} />
      </th>
      <th style={{ cursor: 'pointer' }} onClick={() => handleSort('client_nom')}>
        Client <SortIcon field="client_nom" sortField={sortField} sortDir={sortDir} />
      </th>
      <th style={{ cursor: 'pointer' }} onClick={() => handleSort('statut')}>
        Statut <SortIcon field="statut" sortField={sortField} sortDir={sortDir} />
      </th>
      <th style={{ cursor: 'pointer' }} onClick={() => handleSort('montant_ht')}>
        Montant HT <SortIcon field="montant_ht" sortField={sortField} sortDir={sortDir} />
      </th>
      <th style={{ cursor: 'pointer' }} onClick={() => handleSort('montant_ttc')}>
        Montant TTC <SortIcon field="montant_ttc" sortField={sortField} sortDir={sortDir} />
      </th>
      <th style={{ cursor: 'pointer' }} onClick={() => handleSort('date_echeance')}>
        Échéance <SortIcon field="date_echeance" sortField={sortField} sortDir={sortDir} />
      </th>
      <th>Paiement</th>
      <th>Actions</th>
    </tr>
  </thead>

          <tbody>
            {sortedFactures.map((f) => (
              <tr key={f.id} className={f.statut === 'en_retard' ? 'table-danger' : ''}>
                <td>{f.id}</td>
                <td>
                  <strong>{f.numero_facture}</strong>
                  {f.numero_devis && (
                    <div><small className="text-muted">↳ {f.numero_devis}</small></div>
                  )}
                </td>
                <td>{f.chantier_nom || '-'}</td>
                <td>
                  {f.client_nom}
                  {f.client_entreprise && (
                    <div><small className="text-muted">{f.client_entreprise}</small></div>
                  )}
                </td>
                <td>
                  <Badge bg={STATUT_BADGE[f.statut]?.bg || 'secondary'}>
                    {STATUT_BADGE[f.statut]?.label || f.statut}
                  </Badge>
                </td>
                <td>{formatMontant(f.montant_ht)}</td>
                <td><strong>{formatMontant(f.montant_ttc)}</strong></td>
                <td>{formatDate(f.date_echeance)}</td>
                <td>{formatDate(f.date_paiement)}</td>
                <td>
                  <div className="d-flex gap-1">
                    {/* ✅ Aperçu */}
                    <Button size="sm" variant="outline-info"
                      onClick={() => openApercu(f)} title="Aperçu">
                      👁️
                    </Button>
                    {/* ✅ PDF */}
                    <Button size="sm" variant="outline-success"
                      onClick={() => downloadPdf(f)}
                      disabled={pdfLoading === f.id}
                      title="Télécharger PDF">
                      {pdfLoading === f.id ? <Spinner size="sm" animation="border" /> : '📄'}
                    </Button>
                    {/* Édition */}
                    <Button size="sm" variant="outline-primary"
                      onClick={() => openEditModal(f)} title="Modifier">✏️</Button>
                    {/* Suppression */}
                    {f.statut === 'brouillon' && (
                      <Button size="sm" variant="outline-danger"
                        onClick={() => { setFactureToDelete(f); setShowConfirm(true); }}
                        title="Supprimer">🗑️</Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      {/* ✅ Modal Aperçu */}
      <Modal show={showApercu} onHide={() => setShowApercu(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>👁️ Aperçu — {factureApercu?.numero_facture}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {factureApercu && (
            <div style={{ fontFamily: 'Arial, sans-serif', padding: '20px' }}>
              {/* En-tête */}
              <div className="d-flex justify-content-between align-items-start mb-4 pb-3"
                style={{ borderBottom: '3px solid #1a1a2e' }}>
                <div>
                  <h4 style={{ color: '#1a1a2e' }}>🏗️ BTP Pro Rhône-Alpes</h4>
                  <small className="text-muted">
                    123 rue du Bâtiment, 69000 Lyon<br />
                    contact@btppro.fr — SIRET 123 456 789 00012
                  </small>
                </div>
                <div className="text-end">
                  <h5 style={{ color: '#1a1a2e' }}>{factureApercu.numero_facture}</h5>
                  <small className="text-muted">
                    Émission : {formatDate(factureApercu.date_emission)}<br />
                    Échéance : {formatDate(factureApercu.date_echeance)}
                  </small><br />
                  <Badge bg={STATUT_BADGE[factureApercu.statut]?.bg} className="mt-1">
                    {STATUT_BADGE[factureApercu.statut]?.label}
                  </Badge>
                </div>
              </div>

              {/* Adresses */}
              <Row className="mb-4">
                <Col md={6}>
                  <div className="p-3 rounded" style={{ background: '#f8f9fa' }}>
                    <small className="text-muted text-uppercase fw-bold">Émetteur</small>
                    <p className="mb-0 mt-1">
                      <strong>BTP Pro Rhône-Alpes</strong><br />
                      123 rue du Bâtiment<br />69000 Lyon
                    </p>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="p-3 rounded" style={{ background: '#f8f9fa' }}>
                    <small className="text-muted text-uppercase fw-bold">Facturé à</small>
                    <p className="mb-0 mt-1">
                      <strong>{factureApercu.client_nom}</strong><br />
                      {factureApercu.client_entreprise && <>{factureApercu.client_entreprise}<br /></>}
                    </p>
                  </div>
                </Col>
              </Row>

              {/* Chantier */}
              <div className="p-3 rounded mb-4"
                style={{ background: '#e8f4fd', borderLeft: '4px solid #0d6efd' }}>
                <strong>🏗️ Chantier :</strong> {factureApercu.chantier_nom}
                {factureApercu.numero_devis && (
                  <> — <strong>Devis :</strong> {factureApercu.numero_devis}</>
                )}
              </div>

              {/* Tableau */}
              <Table bordered className="mb-3">
                <thead style={{ background: '#1a1a2e', color: 'white' }}>
                  <tr>
                    <th>Description</th>
                    <th className="text-end">Montant HT</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      <strong>Travaux — {factureApercu.chantier_nom}</strong><br />
                      <small className="text-muted">{factureApercu.notes || 'Prestations selon devis'}</small>
                    </td>
                    <td className="text-end">{formatMontant(factureApercu.montant_ht)}</td>
                  </tr>
                </tbody>
              </Table>

              {/* Totaux */}
              <div className="d-flex justify-content-end mb-4">
                <div style={{ width: '250px' }}>
                  <div className="d-flex justify-content-between py-1"
                    style={{ borderBottom: '1px solid #dee2e6' }}>
                    <span>Montant HT</span>
                    <span>{formatMontant(factureApercu.montant_ht)}</span>
                  </div>
                  <div className="d-flex justify-content-between py-1"
                    style={{ borderBottom: '1px solid #dee2e6' }}>
                    <span>TVA ({factureApercu.tva}%)</span>
                    <span>{formatMontant(
                      (parseFloat(factureApercu.montant_ht || 0) *
                      parseFloat(factureApercu.tva || 20) / 100).toFixed(2)
                    )}</span>
                  </div>
                  <div className="d-flex justify-content-between py-2 fw-bold"
                    style={{ borderBottom: '2px solid #1a1a2e', fontSize: '1.1em' }}>
                    <span>TOTAL TTC</span>
                    <span>{formatMontant(factureApercu.montant_ttc)}</span>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="text-center text-muted mt-3"
                style={{ borderTop: '1px solid #dee2e6', paddingTop: '10px', fontSize: '11px' }}>
                BTP Pro Rhône-Alpes — SIRET 123 456 789 00012 — TVA FR12345678900<br />
                En cas de retard de paiement, des pénalités de 3x le taux légal seront appliquées.
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowApercu(false)}>Fermer</Button>
          <Button variant="success" onClick={() => downloadPdf(factureApercu)}
            disabled={pdfLoading === factureApercu?.id}>
            {pdfLoading === factureApercu?.id
              ? <><Spinner size="sm" animation="border" /> Génération...</>
              : '📄 Télécharger PDF'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal création / édition */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {editFacture ? `✏️ Modifier ${editFacture.numero_facture}` : '📄 Nouvelle Facture'}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            {error && <Alert variant="danger">{error}</Alert>}
            <div className="row">
              <div className="col-md-8">
                <Form.Group className="mb-3">
                  <Form.Label>Chantier *</Form.Label>
                  <Form.Select name="chantier_id" required
                    value={form.chantier_id} onChange={handleChantierChange}
                    disabled={!!editFacture}>
                    <option value="">-- Sélectionner un chantier --</option>
                    {chantiers.map(c => (
                      <option key={c.id} value={c.id}>{c.nom} — {c.client_nom}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </div>
              <div className="col-md-4">
                <Form.Group className="mb-3">
                  <Form.Label>Statut</Form.Label>
                  <Form.Select name="statut" value={form.statut} onChange={handleField}>
                    {STATUTS.map(s => (
                      <option key={s} value={s}>{STATUT_BADGE[s].label}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </div>
            </div>
            <Form.Group className="mb-3">
              <Form.Label>Devis associé <small className="text-muted">(optionnel)</small></Form.Label>
              <Form.Select name="devis_id" value={form.devis_id}
                onChange={handleDevisChange} disabled={!!editFacture}>
                <option value="">-- Aucun devis --</option>
                {devisFiltres.map(d => (
                  <option key={d.id} value={d.id}>
                    {d.numero_devis} — {formatMontant(d.montant_ttc)}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <div className="row">
              <div className="col-md-4">
                <Form.Group className="mb-3">
                  <Form.Label>Montant HT (€) *</Form.Label>
                  <Form.Control type="number" name="montant_ht"
                    min="0" step="0.01" required
                    value={form.montant_ht} onChange={handleField} />
                </Form.Group>
              </div>
              <div className="col-md-4">
                <Form.Group className="mb-3">
                  <Form.Label>TVA (%)</Form.Label>
                  <Form.Select name="tva" value={form.tva} onChange={handleField}>
                    <option value="0">0 % (exonéré)</option>
                    <option value="5.5">5.5 % (réduit)</option>
                    <option value="10">10 % (intermédiaire)</option>
                    <option value="20">20 % (normal)</option>
                  </Form.Select>
                </Form.Group>
              </div>
              <div className="col-md-4">
                <Form.Group className="mb-3">
                  <Form.Label>Montant TTC (€)</Form.Label>
                  <Form.Control type="text"
                    value={`${Number(montantTTC).toLocaleString('fr-FR')} €`}
                    disabled className="bg-light fw-bold" />
                </Form.Group>
              </div>
            </div>
            <div className="row">
              <div className="col-md-4">
                <Form.Group className="mb-3">
                  <Form.Label>Date d'émission</Form.Label>
                  <Form.Control type="date" name="date_emission"
                    value={form.date_emission} onChange={handleField} />
                </Form.Group>
              </div>
              <div className="col-md-4">
                <Form.Group className="mb-3">
                  <Form.Label>Date d'échéance</Form.Label>
                  <Form.Control type="date" name="date_echeance"
                    value={form.date_echeance} onChange={handleField} />
                </Form.Group>
              </div>
              <div className="col-md-4">
                <Form.Group className="mb-3">
                  <Form.Label>Date de paiement</Form.Label>
                  <Form.Control type="date" name="date_paiement"
                    value={form.date_paiement} onChange={handleField}
                    disabled={!['payee'].includes(form.statut)} />
                </Form.Group>
              </div>
            </div>
            <Form.Group className="mb-3">
              <Form.Label>Notes</Form.Label>
              <Form.Control as="textarea" rows={2} name="notes"
                value={form.notes} onChange={handleField} />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>Annuler</Button>
            <Button variant="primary" type="submit" disabled={submitting}>
              {submitting
                ? (editFacture ? 'Mise à jour...' : 'Création...')
                : (editFacture ? 'Mettre à jour' : 'Créer la facture')}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
      
      {/* ✅ Caisse récapitulative */}
      {factures.length > 0 && (
        <div className="mt-4">
          <h5>💰 Récapitulatif global</h5>
          <Table bordered size="sm" style={{ maxWidth: '500px' }}>
            <thead className="table-dark">
              <tr>
                <th>Statut</th>
                <th className="text-end">Nb</th>
                <th className="text-end">Total TTC</th>
              </tr>
            </thead>
            <tbody>
              {STATUTS.map(s => {
                const lignes = factures.filter(f => f.statut === s);
                const total  = lignes.reduce((acc, f) => acc + parseFloat(f.montant_ttc || 0), 0);
                if (lignes.length === 0) return null;
                return (
                  <tr key={s}>
                    <td>
                      <Badge bg={STATUT_BADGE[s].bg}>{STATUT_BADGE[s].label}</Badge>
                    </td>
                    <td className="text-end">{lignes.length}</td>
                    <td className="text-end"><strong>{formatMontant(total)}</strong></td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="table-dark">
                <th>TOTAL</th>
                <th className="text-end">{factures.length}</th>
                <th className="text-end">
                  {formatMontant(
                    factures.reduce((acc, f) => acc + parseFloat(f.montant_ttc || 0), 0)
                  )}
                </th>
              </tr>
            </tfoot>
          </Table>
        </div>
      )}

      {/* Modal confirmation suppression */}
      <Modal show={showConfirm} onHide={() => setShowConfirm(false)} centered></Modal>

      {/* Modal confirmation suppression */}
      <Modal show={showConfirm} onHide={() => setShowConfirm(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>⚠️ Confirmer la suppression</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Supprimer la facture <strong>{factureToDelete?.numero_facture}</strong> ?
          <Alert variant="warning" className="mt-3 mb-0">
            Seules les factures en brouillon peuvent être supprimées.
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfirm(false)}>Annuler</Button>
          <Button variant="danger" onClick={handleDelete}>🗑️ Supprimer</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default FacturesList;
